import React from 'react';
import { useQuery } from 'react-query';
import {
  Activity,
  Banknote,
  CheckCircle2,
  Coins,
  IndianRupee,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { api, endpoints } from '@/services/api';
import { DashboardStats } from '@/types';
import StatsCard from '@/components/common/StatsCard';
import Chart from '@/components/charts/Chart';

const Dashboard: React.FC = () => {
  const { data: response, isLoading: statsLoading, isError, refetch, dataUpdatedAt } = useQuery<{ data: DashboardStats }>(
    'dashboard-stats',
    () => api.get(endpoints.admin.dashboard),
    {
      refetchInterval: 60000,
      staleTime: 30000,
    }
  );

  if (isError && !response?.data) return <div role="alert" className="p-6">Dashboard is unavailable. <button className="underline" onClick={() => refetch()}>Try again</button></div>;
  const stats = response?.data;
  const users = stats?.users;
  const financial = stats?.financial;

  const formatCurrency = (amount: number) => (
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount || 0)
  );

  const formatNumber = (num: number) => new Intl.NumberFormat('en-IN').format(num || 0);

  const profit = financial?.profit || 0;
  const todayProfit = financial?.todayProfit || 0;

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto animate-fade-in">
      {isError && <div role="alert" className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800">Dashboard could not refresh. {dataUpdatedAt ? `Last updated ${new Date(dataUpdatedAt).toLocaleTimeString()}.` : 'Figures are unavailable.'} <button onClick={() => refetch()} className="underline">Retry</button></div>}
      <div className="mb-4 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1 sm:mb-2">Dashboard</h1>
        <p className="text-sm sm:text-base text-slate-600">
          Real-time platform money, users, coin purchase, withdrawal, and profit overview.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-6">
        <StatsCard
          title="Today Received"
          value={formatCurrency(financial?.todayReceived || 0)}
          trend={todayProfit >= 0 ? 'up' : 'down'}
          trendValue={`Net ${formatCurrency(todayProfit)}`}
          icon={<IndianRupee className="w-5 h-5 sm:w-6 sm:h-6" />}
          color="green"
          loading={statsLoading}
        />
        <StatsCard
          title="Today Withdrawn"
          value={formatCurrency(financial?.todayWithdrawn || 0)}
          trend="down"
          trendValue={`${formatNumber(financial?.todayPaidWithdrawals || 0)} payouts`}
          icon={<Wallet className="w-5 h-5 sm:w-6 sm:h-6" />}
          color="yellow"
          loading={statsLoading}
        />
        <StatsCard
          title="Total Profit"
          value={formatCurrency(profit)}
          trend={profit >= 0 ? 'up' : 'down'}
          trendValue="Received - paid out"
          icon={<TrendingUp className="w-5 h-5 sm:w-6 sm:h-6" />}
          color={profit >= 0 ? 'purple' : 'red'}
          loading={statsLoading}
        />
        <StatsCard
          title="Total Users"
          value={formatNumber(users?.total || 0)}
          trend="up"
          trendValue={`${formatNumber(users?.newToday || 0)} new today`}
          icon={<Users className="w-5 h-5 sm:w-6 sm:h-6" />}
          color="blue"
          loading={statsLoading}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-8">
        <StatsCard
          title="Approved Purchase"
          value={formatCurrency(financial?.totalApprovedPurchaseMoney || 0)}
          trend="up"
          trendValue={`${formatNumber(financial?.totalApprovedPurchases || 0)} approved`}
          icon={<CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6" />}
          color="green"
          loading={statsLoading}
        />
        <StatsCard
          title="Total Withdrawn"
          value={formatCurrency(financial?.totalWithdrawnMoney || 0)}
          trend="down"
          trendValue={`${formatNumber(financial?.totalPaidWithdrawals || 0)} paid`}
          icon={<Banknote className="w-5 h-5 sm:w-6 sm:h-6" />}
          color="yellow"
          loading={statsLoading}
        />
        <StatsCard
          title="Coins Sold"
          value={formatNumber(financial?.totalCoinsSold || 0)}
          trend="up"
          trendValue={`${formatNumber(financial?.uniqueBuyers || 0)} buyers`}
          icon={<Coins className="w-5 h-5 sm:w-6 sm:h-6" />}
          color="purple"
          loading={statsLoading}
        />
        <StatsCard
          title="Active Users"
          value={formatNumber(users?.active || 0)}
          trend="up"
          trendValue={`${formatNumber(users?.activeToday || 0)} active today`}
          icon={<Activity className="w-5 h-5 sm:w-6 sm:h-6" />}
          color="blue"
          loading={statsLoading}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-8">
        <div className="xl:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-slate-900">Daily Collection</h3>
              <p className="text-xs sm:text-sm text-slate-500">Approved coin purchases, paid withdrawals, and net profit.</p>
            </div>
          </div>
          <Chart
            data={financial?.dailyCollections || []}
            height={typeof window !== 'undefined' && window.innerWidth < 640 ? 220 : 320}
            loading={statsLoading}
            type="bar"
            dataKeys={['receivedAmount', 'withdrawnAmount', 'profit']}
            xAxisKey="date"
            colors={['#10b981', '#f59e0b', '#8b5cf6']}
          />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-3 sm:mb-4">Today</h3>
          <div className="space-y-1">
            <MetricRow label="Money received" value={formatCurrency(financial?.todayReceived || 0)} />
            <MetricRow label="Money withdrawn" value={formatCurrency(financial?.todayWithdrawn || 0)} />
            <MetricRow label="Today profit" value={formatCurrency(todayProfit)} valueClass={todayProfit >= 0 ? 'text-green-600' : 'text-red-600'} />
            <MetricRow label="Approved purchases" value={formatNumber(financial?.todayApprovedPurchases || 0)} />
            <MetricRow label="People bought coins" value={formatNumber(financial?.todayUniqueBuyers || 0)} />
            <MetricRow label="Coins sold today" value={formatNumber(financial?.todayCoinsSold || 0)} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <SummaryPanel
          title="Users"
          rows={[
            ['Total users', formatNumber(users?.total || 0)],
            ['Active accounts', formatNumber(users?.active || 0)],
            ['Active today', formatNumber(users?.activeToday || 0)],
            ['New this week', formatNumber(users?.newThisWeek || 0)],
          ]}
        />
        <SummaryPanel
          title="Coin Purchases"
          rows={[
            ['Approved purchase money', formatCurrency(financial?.totalApprovedPurchaseMoney || 0)],
            ['Approved purchases', formatNumber(financial?.totalApprovedPurchases || 0)],
            ['Unique buyers', formatNumber(financial?.uniqueBuyers || 0)],
            ['Bonus coins included', formatNumber(financial?.totalBonusCoinsSold || 0)],
          ]}
        />
        <SummaryPanel
          title="Withdrawals"
          rows={[
            ['Total paid out', formatCurrency(financial?.totalWithdrawnMoney || 0)],
            ['Paid withdrawals', formatNumber(financial?.totalPaidWithdrawals || 0)],
            ['Pending requests', formatNumber(financial?.pendingWithdrawalCount || 0)],
            ['Approved, not paid', formatNumber(financial?.approvedWithdrawalCount || 0)],
          ]}
        />
      </div>

      <div className="mt-4 sm:mt-8 bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4">
        <div className="flex items-start space-x-3">
          <Activity className="w-5 h-5 mt-0.5 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-900">Live Admin Data</p>
            <p className="text-xs mt-1 text-blue-700">
              This dashboard refreshes every 60 seconds. Received money is approved coin purchases; withdrawn money is paid withdrawal payouts.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const MetricRow = ({ label, value, valueClass = 'text-slate-900' }: { label: string; value: string; valueClass?: string }) => (
  <div className="flex items-center justify-between py-2.5 sm:py-3 border-b border-slate-100 last:border-b-0">
    <span className="text-xs sm:text-sm text-slate-600">{label}</span>
    <span className={`text-xs sm:text-sm font-semibold ${valueClass}`}>{value}</span>
  </div>
);

const SummaryPanel = ({ title, rows }: { title: string; rows: Array<[string, string]> }) => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 sm:p-6">
    <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-3 sm:mb-4">{title}</h3>
    <div className="space-y-1">
      {rows.map(([label, value]) => (
        <MetricRow key={label} label={label} value={value} />
      ))}
    </div>
  </div>
);

export default Dashboard;
